/**
 * Sentry-instrumented fetch wrapper for Supabase.
 *
 * Pass `createSentryFetch()` as `global.fetch` to Supabase's `createClient`
 * to automatically capture API errors (4xx/5xx) without modifying calling code.
 *
 * What it does:
 * - Reports 4xx/5xx as proper Error objects (triggers session replay capture)
 * - Adds breadcrumbs for ALL Supabase calls (success + failure) so crash
 *   timelines show the full API call history leading up to an issue
 * - Includes request body context for mutations (POST/PATCH/DELETE)
 * - Fingerprints by operation + status + normalized error message
 *
 * Portable — copy this file into any project that uses Supabase + Sentry.
 */
import * as Sentry from "@sentry/react";

/** Status codes that should never be reported (expected in normal flow). */
const IGNORED_STATUS_CODES = new Set([
  401, // unauthenticated — handled by auth refresh / redirect
  406, // PostgREST "no rows" when using .single() — application-level, not a bug
]);

/** URL path patterns to never report errors for. */
const IGNORED_PATH_PATTERNS: RegExp[] = [
  /\/auth\/v1\/token/, // auth token refresh failures are retried automatically
];

/** HTTP methods that carry a meaningful request body. */
const MUTATION_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

/** Max length for request/response body strings stored in Sentry context. */
const MAX_BODY_LENGTH = 2048;

/**
 * Extracts a short, groupable operation label from a Supabase request.
 * e.g. "POST /rest/v1/cars" or "GET /storage/v1/object/bucket/file.png"
 */
const getOperationLabel = (method: string, url: URL): string => {
  const pathname = url.pathname
    .replace(
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
      ":id",
    )
    .replace(/\/\d+(?=\/|$)/g, "/:id");

  return `${method.toUpperCase()} ${pathname}`;
};

/**
 * Truncates a string to MAX_BODY_LENGTH, appending "…" if truncated.
 */
const truncate = (value: string): string =>
  value.length > MAX_BODY_LENGTH ? value.slice(0, MAX_BODY_LENGTH) + "…" : value;

/**
 * Tries to extract the request body as a string for context.
 */
const extractRequestBody = (init: RequestInit | undefined): string | undefined => {
  if (!init?.body) return undefined;

  if (typeof init.body === "string") return truncate(init.body);

  // FormData, Blob, ArrayBuffer, etc. — not worth serializing
  return undefined;
};

/**
 * Reads the response body and tries to pull a structured error message.
 * Returns both the raw (truncated) body and the extracted message.
 */
const extractResponseInfo = async (
  response: Response,
): Promise<{ body?: string; message?: string }> => {
  try {
    const cloned = response.clone();
    const text = await cloned.text();

    if (!text) return {};

    const body = truncate(text);

    try {
      const json = JSON.parse(text) as Record<string, unknown>;
      const message =
        (json.message as string) ??
        (json.error as string) ??
        (json.msg as string) ??
        (json.error_description as string);
      if (typeof message === "string") return { body, message };
    } catch {
      // Not JSON
    }

    return { body };
  } catch {
    return {};
  }
};

/**
 * Normalizes an error message for use in fingerprinting.
 * Strips dynamic values (IDs, timestamps, specific field values) so that
 * the same category of error groups together.
 */
const normalizeForFingerprint = (message: string): string =>
  message
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, ":id")
    .replace(/\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}[:\d.]*/g, ":timestamp")
    .replace(/\b\d+\b/g, ":n")
    .trim()
    .slice(0, 200);

/**
 * Resolves the URL string from the various shapes `fetch` accepts as input.
 */
const resolveUrl = (input: RequestInfo | URL): string => {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.href;
  return (input as Request).url;
};

/**
 * Returns a `fetch` function that wraps the native `fetch`, adds breadcrumbs
 * for all Supabase calls, and reports HTTP errors to Sentry as exceptions
 * (which triggers session replay capture).
 */
export const createSentryFetch = (): typeof globalThis.fetch => {
  const nativeFetch = globalThis.fetch.bind(globalThis);

  const sentryFetch: typeof globalThis.fetch = async (input, init) => {
    const method = (init?.method ?? "GET").toUpperCase();
    let url: URL;

    try {
      url = new URL(resolveUrl(input));
    } catch {
      // If URL parsing fails, just pass through to native fetch
      return nativeFetch(input, init);
    }

    const operation = getOperationLabel(method, url);
    const response = await nativeFetch(input, init);

    // ── Breadcrumb for every call (success + failure) ──────────────
    Sentry.addBreadcrumb({
      type: "http",
      category: "supabase",
      message: `${operation} → ${response.status}`,
      level: response.ok ? "info" : response.status >= 500 ? "error" : "warning",
      data: {
        method,
        url: url.toString(),
        status_code: response.status,
      },
    });

    // ── Only report errors from here ───────────────────────────────
    if (response.ok) return response;
    if (IGNORED_STATUS_CODES.has(response.status)) return response;
    if (IGNORED_PATH_PATTERNS.some((p) => p.test(url.pathname))) return response;

    const { body: responseBody, message: errorMessage } = await extractResponseInfo(response);
    const requestBody = MUTATION_METHODS.has(method) ? extractRequestBody(init) : undefined;

    Sentry.withScope((scope) => {
      const level = response.status >= 500 ? "error" : "warning";
      scope.setLevel(level);

      scope.setTag("http.status_code", response.status);
      scope.setTag("http.method", method);
      scope.setTag("supabase.operation", operation);

      scope.setContext("Supabase Request", {
        method,
        url: url.toString(),
        pathname: url.pathname,
        status: response.status,
        statusText: response.statusText,
        ...(requestBody ? { requestBody } : {}),
      });

      if (responseBody) {
        scope.setContext("Supabase Response", {
          body: responseBody,
          ...(errorMessage ? { message: errorMessage } : {}),
        });
      }

      // Fingerprint: operation + status + normalized error message (if any)
      const fingerprint = [
        "supabase-http-error",
        operation,
        String(response.status),
      ];
      if (errorMessage) {
        fingerprint.push(normalizeForFingerprint(errorMessage));
      }
      scope.setFingerprint(fingerprint);

      // Use captureException with a real Error so session replay is triggered
      const error = new Error(`Supabase ${response.status}: ${operation}${errorMessage ? ` — ${errorMessage}` : ""}`);
      error.name = "SupabaseHttpError";
      Sentry.captureException(error);
    });

    return response;
  };

  return sentryFetch;
};
